import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../store/hooks';
import { edgeClient } from '../api/client';

export default function Contact() {
  const { ref, isInView } = useInView(0.1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    project: '',
    budget: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await edgeClient.api.fetch('/api/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          projectType: formData.project || undefined,
          budget: formData.budget || undefined,
          message: formData.message,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-white/[0.04] border border-white/10 rounded-xl text-white font-body placeholder-surface-600 focus:outline-none focus:border-[#c8ff00]/40 focus:ring-1 focus:ring-[#c8ff00]/20 transition-all';

  return (
    <section id="contact" ref={ref} className="py-28 sm:py-36 relative">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      {/* Glow effect */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#c8ff00]/[0.03] rounded-full blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <span className="inline-block text-xs font-body font-semibold tracking-[0.25em] uppercase text-[#c8ff00] mb-4">
              Get in Touch
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Ready to build something
              <br />
              <span className="text-gradient italic">extraordinary?</span>
            </h2>
            <p className="font-body text-surface-400 text-lg leading-relaxed mb-8">
              Whether you're launching a new brand, redesigning an existing site, or building a complex platform — we'd love to hear about it. Tell us your vision and we'll handle the rest.
            </p>

            {/* Contact info */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#c8ff00]/10 flex items-center justify-center text-[#c8ff00]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <span className="font-body text-surface-300">hello@primeweb.it.com</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#c8ff00]/10 flex items-center justify-center text-[#c8ff00]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <span className="font-body text-surface-300">Available worldwide — fully remote</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#c8ff00]/10 flex items-center justify-center text-[#c8ff00]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <span className="font-body text-surface-300">Typical response within 24 hours</span>
              </div>
            </div>
          </motion.div>

          {/* Right column — form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            {submitted ? (
              <div className="glass-card rounded-2xl p-8 sm:p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-[#c8ff00]/20 flex items-center justify-center mx-auto mb-6">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#c8ff00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="font-display text-2xl font-bold text-white mb-2">Message Sent!</h3>
                <p className="font-body text-surface-400">
                  Thank you for reaching out. We'll get back to you within 24 hours with a tailored response.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="glass-card rounded-2xl p-8 sm:p-10 space-y-5">
                {error && (
                  <div className="px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <p className="font-body text-sm text-red-400">{error}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body text-sm font-medium text-surface-300 mb-2">Full Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={inputClass}
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-surface-300 mb-2">Email Address</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={inputClass}
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-body text-sm font-medium text-surface-300 mb-2">Phone Number <span className="text-surface-600">(optional)</span></label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className={inputClass}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                </div>

                <div>
                  <label className="block font-body text-sm font-medium text-surface-300 mb-2">Project Type</label>
                  <select
                    value={formData.project}
                    onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                    className={`${inputClass} appearance-none`}
                  >
                    <option value="" className="bg-surface-800">Select a service</option>
                    <option value="website" className="bg-surface-800">Website Design</option>
                    <option value="uiux" className="bg-surface-800">UI/UX Design</option>
                    <option value="landing" className="bg-surface-800">Landing Page</option>
                    <option value="saas" className="bg-surface-800">SaaS Platform</option>
                    <option value="other" className="bg-surface-800">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block font-body text-sm font-medium text-surface-300 mb-2">Tell Us About Your Project</label>
                  <textarea
                    required
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className={`${inputClass} resize-none`}
                    placeholder="Describe your project, goals, and timeline..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-[#c8ff00] text-surface-900 font-semibold font-body rounded-xl hover:bg-[#d4ff33] transition-all duration-300 hover:shadow-lg hover:shadow-[#c8ff00]/20 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
                <p className="font-body text-xs text-surface-600 text-center">
                  No spam. No commitments. Just a conversation about your next project.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
